import React, { useState } from "react";
import clsx from "clsx";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";

import Box from "@material-ui/core/Box";
import CallMadeIcon from "@material-ui/icons/CallMade";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import routes from "common/routes";
import Dialog from "components/Dialog/Dialog";
import HorseShoeIcon from "components/Icons/HorseShoe";
import Link from "components/Link";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { SortOrder } from "interfaces/SortOrder";
import useStyles from "./styles";

const ListsInfoBlock = () => {
  const classes = useStyles();
  const { currentUser } = useLoggedInUser();

  const [horsesOnListsOpen, setHorsesOnListsOpen] = useState<boolean>(false);

  const selectedFacilityIds = useAppSelector(
    state => state?.queriesConfig?.selectedFacilityIds
  );

  const { useListHorseOnLists, usePullHorsesFromListsCount } =
    useRacehorse360Api();

  const trainerId = currentUser.trainerIds[0];
  const { isLoading: isCountLoading, data: countData } =
    usePullHorsesFromListsCount({
      trainerId,
      horseFacilityIds: selectedFacilityIds
    });

  const listsCount = countData?.count || 0;

  const { isLoading: isDataLoading, data: horseOnListsData } =
    useListHorseOnLists(
      `${trainerId}-horses-on-lists`,
      {
        query: {
          trainerId: { value: trainerId },
          activeOnly: { value: true }
        },
        pagingOptions: {
          maxResults: 9999,
          offset: 0
        },
        getOptions: {
          select: ["horse.id", "horse.name", "list_type", "date_on"],
          orderBy: [`dateOn ${SortOrder.ASC}`]
        }
      },
      {
        enabled: horsesOnListsOpen
      }
    );

  const mappedHorseOnLists =
    horseOnListsData?.horseOnLists.reduce((acc, cur) => {
      if (acc[cur.horse.id]) {
        acc[cur.horse.id] = [...acc[cur.horse.id], cur];
      } else {
        acc[cur.horse.id] = [cur];
      }
      return acc;
    }, {}) || {};
  const sorted = Object.values(mappedHorseOnLists).sort(
    (l1: racehorse360.IHorseOnList[], l2: racehorse360.IHorseOnList[]) =>
      parseISO(l2[l2.length - 1].dateOn).getTime() -
      parseISO(l1[l1.length - 1].dateOn).getTime()
  );

  const handleHorsesOnListsClick = () => {
    if (!isCountLoading && listsCount) {
      setHorsesOnListsOpen(true);
    }
  };

  const handleHorsesOnListsClose = () => {
    setHorsesOnListsOpen(false);
  };

  return (
    <>
      <Grid
        component={Paper}
        container
        className={clsx(classes.infoPaper, {
          [classes.clickable]: !isCountLoading && listsCount
        })}
        onClick={handleHorsesOnListsClick}
      >
        {isCountLoading ? (
          <Loader />
        ) : (
          <>
            <Grid
              item
              className={clsx(classes.badge, classes.horsesOnListsBadge)}
            >
              <HorseShoeIcon className={classes.badgeIcon} />
            </Grid>
            <Grid item>
              <Grid container>
                <Grid
                  component={Typography}
                  item
                  className={classes.count}
                  xs={12}
                >
                  {listsCount}
                </Grid>
                <Grid
                  component={Typography}
                  item
                  className={classes.label}
                  xs={12}
                >
                  Horses On Lists
                </Grid>
              </Grid>
            </Grid>
            {Boolean(listsCount) && (
              <Box className={classes.corner}>
                <CallMadeIcon className={classes.cornerIcon} />
              </Box>
            )}
          </>
        )}
      </Grid>

      {Boolean(listsCount) && (
        <Dialog
          title={`${listsCount} Horses On Lists`}
          onClose={handleHorsesOnListsClose}
          className={classes.listsModal}
          isOpen={horsesOnListsOpen}
        >
          {isDataLoading ? (
            <Loader className={classes.dialogLoader} />
          ) : (
            <div>
              {sorted.map((horseOnLists: racehorse360.IHorseOnList[], i) => {
                const horseId = horseOnLists[0].horse.id;

                return (
                  horseOnLists?.length && (
                    <Grid
                      key={`${horseId}-${i}`}
                      className={classes.listTableRow}
                      container
                      spacing={0}
                      component={Link}
                      to={routes.horseDetails.path.replace(":horseId", horseId)}
                    >
                      <Grid
                        item
                        xs={5}
                        className={clsx(
                          classes.listsTableCell,
                          classes.listsTableCellName
                        )}
                      >
                        {horseOnLists[0].horse.name}
                      </Grid>
                      <Grid container item xs={7}>
                        {horseOnLists.map(
                          (list: racehorse360.IHorseOnList, j) => (
                            <Grid
                              container
                              key={`${horseId}-${list.listType}-${i}-${j}`}
                              className={classes.contentRows}
                            >
                              <Grid
                                item
                                xs={7}
                                className={classes.listsTableCell}
                              >
                                {list.listType}
                              </Grid>
                              <Grid
                                item
                                xs={5}
                                className={clsx(
                                  classes.listsTableCell,
                                  classes.listsTableCellDate
                                )}
                              >
                                {format(parseISO(list.dateOn), "M/d/yy")}
                              </Grid>
                            </Grid>
                          )
                        )}
                      </Grid>
                    </Grid>
                  )
                );
              })}
            </div>
          )}
        </Dialog>
      )}
    </>
  );
};

export default ListsInfoBlock;
